/**
 * GraphQL User Service
 * Provides user management operations using the .NET GraphQL API.
 * Maintains compatibility with existing frontend interfaces.
 *
 * Logging strategy:
 * - INFO: Profile loads, CRUD operations on users/service providers
 * - WARN: Auth failures, missing data, authorization denials
 * - ERROR: Unexpected exceptions
 * - DEBUG: Cache hits, role checks, intermediate operations
 */

import logger from '../logger';
import { graphqlClient, extractData, getAuthToken } from './client';
import {
  ME_QUERY,
  ALL_USERS_QUERY,
  SERVICE_PROVIDERS_QUERY,
  SERVICE_PROVIDER_BY_KEY_QUERY,
  UPDATE_USER_PROFILE_MUTATION,
  CREATE_SERVICE_PROVIDER_ADMIN_MUTATION,
  CREATE_SERVICE_PROVIDER_MUTATION,
  UPDATE_SERVICE_PROVIDER_MUTATION,
  DELETE_SERVICE_PROVIDER_MUTATION,
  REGISTER_MUTATION,
  UPDATE_USER_ROLE_MUTATION,
  UPDATE_USER_STATUS_MUTATION,
  ASSIGN_USER_TO_SERVICE_PROVIDER_MUTATION,
} from './queries';
import type {
  GraphQLUser,
  GraphQLServiceProvider,
  MeResponse,
  AllUsersResponse,
  ServiceProvidersResponse,
  ServiceProviderByKeyResponse,
  UpdateUserInput,
  UserRole,
  UpdateUserRoleInput,
  UpdateUserStatusInput,
  AssignUserToServiceProviderInput,
  UpdateUserRoleResponse,
  UpdateUserStatusResponse,
  AssignUserToServiceProviderResponse,
  CreateServiceProviderInput,
  UpdateServiceProviderInput,
  CreateServiceProviderResponse,
  UpdateServiceProviderResponse,
  DeleteServiceProviderResponse,
} from './types';

// ============================================
// Type Definitions for Frontend Compatibility
// ============================================

/**
 * Frontend UserProfile interface
 * Maps GraphQL response to existing frontend format
 */
export interface UserProfile {
  id: string;
  email: string;
  full_name?: string;
  role: 'customer' | 'serviceprovideradmin' | 'superadmin';
  is_active: boolean;
  phone_number?: string;
  serviceprovider_id?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Frontend ServiceProvider interface
 * Maps GraphQL response to existing frontend format
 */
export interface ServiceProvider {
  id: string;
  serviceprovider_name: string;
  service_key: string;
  contact_email: string;
  contact_phone?: string;
  primary_color: string;
  logo_url?: string;
  is_active: boolean;
  max_distance?: number;
  default_location?: { lat: number; lng: number } | string;
  default_zoom?: number;
  timezone?: string;
  attributes?: Record<string, unknown> | string;
  created_at: string;
  updated_at: string;
}

/**
 * User roles interface for role checking
 */
export interface UserRoles {
  isSuperAdmin: boolean;
  isServiceProviderAdmin: boolean;
  isBaseService?: boolean;
  isCustomer: boolean;
}

// ============================================
// Type Mapping Functions
// ============================================

/**
 * Map GraphQL role to frontend role format
 */
function mapRole(role: UserRole): 'customer' | 'serviceprovideradmin' | 'superadmin' {
  const roleMap: Record<UserRole, 'customer' | 'serviceprovideradmin' | 'superadmin'> = {
    CUSTOMER: 'customer',
    SERVICE_PROVIDER_ADMIN: 'serviceprovideradmin',
    SUPER_ADMIN: 'superadmin',
  };
  return roleMap[role] || 'customer';
}

/**
 * Convert GraphQL user to frontend UserProfile format
 */
function toUserProfile(gqlUser: GraphQLUser): UserProfile {
  return {
    id: gqlUser.id,
    email: gqlUser.email,
    full_name: gqlUser.fullName,
    role: mapRole(gqlUser.role),
    is_active: gqlUser.isActive,
    phone_number: gqlUser.phoneNumber,
    serviceprovider_id: gqlUser.serviceProviderId,
    created_at: gqlUser.createdAt,
    updated_at: gqlUser.updatedAt,
  };
}

/**
 * Convert GraphQL service provider to frontend format
 */
function toServiceProvider(gqlProvider: GraphQLServiceProvider): ServiceProvider {
  // Parse defaultLocation if it's a string
  let defaultLocation = gqlProvider.defaultLocation;
  if (typeof defaultLocation === 'string') {
    try {
      defaultLocation = JSON.parse(defaultLocation);
    } catch {
      // Keep as string if parsing fails
    }
  }

  // Parse attributesJson - JSON string from GraphQL
  let attributes: Record<string, unknown> | undefined = undefined;
  if (gqlProvider.attributesJson) {
    try {
      attributes = JSON.parse(gqlProvider.attributesJson);
    } catch {
      logger.warn('Failed to parse attributesJson');
    }
  }

  return {
    id: gqlProvider.id,
    serviceprovider_name: gqlProvider.serviceProviderName,
    service_key: gqlProvider.serviceKey,
    contact_email: gqlProvider.contactEmail,
    contact_phone: gqlProvider.contactPhone,
    primary_color: gqlProvider.primaryColor,
    logo_url: gqlProvider.logoUrl,
    is_active: gqlProvider.isActive,
    max_distance: gqlProvider.maxDistance,
    default_location: defaultLocation as { lat: number; lng: number } | string | undefined,
    default_zoom: gqlProvider.defaultZoom,
    timezone: gqlProvider.timezone,
    attributes: attributes,
    created_at: gqlProvider.createdAt,
    updated_at: gqlProvider.updatedAt,
  };
}

// ============================================
// Cached User Profile
// ============================================

let cachedUserProfile: UserProfile | null = null;

/**
 * Clear the cached user profile
 */
export function clearUserProfileCache(): void {
  cachedUserProfile = null;
}

// ============================================
// User Profile Functions
// ============================================

/**
 * Gets the current user's profile including role information
 */
export const getCurrentUserProfile = async (): Promise<{
  data: UserProfile | null;
  error: Error | null;
}> => {
  logger.debug('getCurrentUserProfile called');

  // Check if user is authenticated
  const token = getAuthToken();
  if (!token) {
    // DEBUG: No token is normal for unauthenticated visitors
    logger.debug('No authentication token found');
    return { data: null, error: new Error('No active session. User is not logged in.') };
  }

  // Return cached profile if available
  if (cachedUserProfile) {
    logger.debug('Returning cached user profile', { userId: cachedUserProfile.id });
    return { data: cachedUserProfile, error: null };
  }

  try {
    const response = await graphqlClient.authenticatedQuery<MeResponse>(ME_QUERY);
    const { data, error } = extractData(response);

    if (error) {
      // WARN: Failed to fetch profile - may indicate expired token
      logger.warn('Error fetching user profile', { error });
      return { data: null, error: new Error(error) };
    }

    if (!data?.me) {
      logger.warn('No profile found for authenticated user');
      return { data: null, error: new Error('User profile not found') };
    }

    const profile = toUserProfile(data.me);
    cachedUserProfile = profile;
    // INFO: User profile loaded successfully
    logger.info('User profile loaded', { userId: profile.id, role: profile.role });
    return { data: profile, error: null };
  } catch (error) {
    // ERROR: Unexpected exception fetching profile
    logger.error('Unexpected error getting user profile', error as Error);
    return { data: null, error: error as Error };
  }
};

/**
 * Checks if the current user has a specific role
 */
export const hasRole = async (
  role: 'customer' | 'serviceprovideradmin' | 'superadmin'
): Promise<boolean> => {
  logger.debug('Checking user role', { requestedRole: role });

  try {
    const { data: profile, error } = await getCurrentUserProfile();

    if (error || !profile) {
      logger.debug('No user profile found for role check');
      return false;
    }

    logger.debug('Role check result', { userRole: profile.role, requestedRole: role });
    return profile.role === role;
  } catch (err) {
    logger.error('Unexpected error in hasRole', err as Error);
    return false;
  }
};

/**
 * Checks if current user is a superadmin
 */
export const isSuperAdmin = async (): Promise<boolean> => {
  return hasRole('superadmin');
};

/**
 * Checks if current user is a service provider admin
 */
export const isServiceProviderAdmin = async (): Promise<boolean> => {
  return hasRole('serviceprovideradmin');
};

/**
 * Alias for isServiceProviderAdmin (used in some components)
 */
export const isBaseService = async (): Promise<boolean> => {
  return isServiceProviderAdmin();
};

// ============================================
// Service Provider Functions
// ============================================

/**
 * Gets company information for the current user (if they are a service provider admin)
 */
export const getCurrentUserServiceProvider = async (): Promise<{
  data: ServiceProvider | null;
  error: Error | null;
}> => {
  try {
    // First get the current user's profile
    const { data: profile, error: profileError } = await getCurrentUserProfile();

    if (profileError || !profile || !profile.serviceprovider_id) {
      return {
        data: null,
        error: profileError || new Error('User has no associated service provider'),
      };
    }

    // Get all service providers and find the matching one
    const { data: providers } = await getAllServiceProviders(true);

    if (!providers) {
      return { data: null, error: new Error('Could not fetch service providers') };
    }

    const serviceProvider = providers.find((sp) => sp.id === profile.serviceprovider_id);

    if (!serviceProvider) {
      return { data: null, error: new Error('Service provider not found') };
    }

    return { data: serviceProvider, error: null };
  } catch (error) {
    logger.error('Error getting service provider', error as Error);
    return { data: null, error: error as Error };
  }
};

/**
 * Gets all service providers
 */
export const getAllServiceProviders = async (
  isAdminOverride?: boolean
): Promise<{ data: ServiceProvider[] | null; error: Error | null }> => {
  try {
    // Check authorization unless overridden
    if (!isAdminOverride) {
      const isAdmin = await isSuperAdmin();
      if (!isAdmin) {
        return {
          data: null,
          error: new Error('Unauthorized: Only superadmins can view all service providers'),
        };
      }
    }

    // Include inactive service providers for admin views
    const response = await graphqlClient.query<ServiceProvidersResponse>(
      SERVICE_PROVIDERS_QUERY,
      { includeInactive: isAdminOverride || false }
    );
    const { data, error } = extractData(response);

    if (error) {
      return { data: null, error: new Error(error) };
    }

    return {
      data: (data?.serviceProviders || []).map(toServiceProvider),
      error: null,
    };
  } catch (error) {
    logger.error('Error getting all service providers', error as Error);
    return { data: null, error: error as Error };
  }
};

/**
 * Gets a service provider by service key
 */
export const getServiceProviderByKey = async (
  serviceKey: string
): Promise<{ data: ServiceProvider | null; error: Error | null }> => {
  try {
    const response = await graphqlClient.query<ServiceProviderByKeyResponse>(
      SERVICE_PROVIDER_BY_KEY_QUERY,
      { serviceKey }
    );
    const { data, error } = extractData(response);

    if (error) {
      return { data: null, error: new Error(error) };
    }

    if (!data?.serviceProviderByKey) {
      return { data: null, error: null };
    }

    return {
      data: toServiceProvider(data.serviceProviderByKey),
      error: null,
    };
  } catch (error) {
    logger.error('Error getting service provider by key', error as Error);
    return { data: null, error: error as Error };
  }
};

// ============================================
// Service Provider Mutation Functions
// ============================================

/**
 * Creates a new service provider (superadmin only)
 */
export const createServiceProvider = async (
  input: CreateServiceProviderInput
): Promise<{ data: ServiceProvider | null; error: Error | null }> => {
  try {
    logger.info('createServiceProvider called', { serviceProviderName: input.serviceProviderName });

    const response = await graphqlClient.authenticatedMutate<CreateServiceProviderResponse>(
      CREATE_SERVICE_PROVIDER_MUTATION,
      { input }
    );

    const { data, error } = extractData(response);

    if (error) {
      logger.error('Error creating service provider', undefined, { error });
      return { data: null, error: new Error(error) };
    }

    if (!data?.createServiceProvider?.serviceProvider) {
      return { data: null, error: new Error('No service provider returned from mutation') };
    }

    const serviceProvider = toServiceProvider(data.createServiceProvider.serviceProvider);
    logger.info('Service provider created', { id: serviceProvider.id, name: serviceProvider.serviceprovider_name });
    return { data: serviceProvider, error: null };
  } catch (error) {
    logger.error('Exception in createServiceProvider', error as Error);
    return { data: null, error: error as Error };
  }
};

/**
 * Updates an existing service provider (superadmin only)
 */
export const updateServiceProvider = async (
  input: UpdateServiceProviderInput
): Promise<{ data: ServiceProvider | null; error: Error | null }> => {
  try {
    logger.info('updateServiceProvider called', { id: input.id });

    const response = await graphqlClient.authenticatedMutate<UpdateServiceProviderResponse>(
      UPDATE_SERVICE_PROVIDER_MUTATION,
      { input }
    );

    const { data, error } = extractData(response);

    if (error) {
      logger.error('Error updating service provider', undefined, { error });
      return { data: null, error: new Error(error) };
    }

    if (!data?.updateServiceProvider?.serviceProvider) {
      return { data: null, error: new Error('No service provider returned from mutation') };
    }

    const serviceProvider = toServiceProvider(data.updateServiceProvider.serviceProvider);
    logger.info('Service provider updated', { id: serviceProvider.id, name: serviceProvider.serviceprovider_name });
    return { data: serviceProvider, error: null };
  } catch (error) {
    logger.error('Exception in updateServiceProvider', error as Error);
    return { data: null, error: error as Error };
  }
};

/**
 * Deletes (deactivates) a service provider (superadmin only)
 */
export const deleteServiceProvider = async (
  id: string
): Promise<{ success: boolean; error: Error | null }> => {
  try {
    logger.info('deleteServiceProvider called', { id });

    const response = await graphqlClient.authenticatedMutate<DeleteServiceProviderResponse>(
      DELETE_SERVICE_PROVIDER_MUTATION,
      { id }
    );

    const { data, error } = extractData(response);

    if (error) {
      logger.error('Error deleting service provider', undefined, { error, id });
      return { success: false, error: new Error(error) };
    }

    if (!data?.deleteServiceProvider?.success) {
      return { success: false, error: new Error(data?.deleteServiceProvider?.message || 'Delete failed') };
    }

    logger.info('Service provider deleted/deactivated', { id });
    return { success: true, error: null };
  } catch (error) {
    logger.error('Exception in deleteServiceProvider', error as Error);
    return { success: false, error: error as Error };
  }
};

// ============================================
// User Management Functions
// ============================================

/**
 * Gets all users (for superadmins only)
 */
export const getAllUsers = async (
  isAdminOverride?: boolean
): Promise<{ data: UserProfile[] | null; error: Error | null }> => {
  try {
    // Check authorization unless overridden
    if (!isAdminOverride) {
      const isAdmin = await isSuperAdmin();
      if (!isAdmin) {
        return {
          data: null,
          error: new Error('Unauthorized: Only superadmins can view all users'),
        };
      }
    }

    const response = await graphqlClient.authenticatedQuery<AllUsersResponse>(ALL_USERS_QUERY);
    const { data, error } = extractData(response);

    if (error) {
      return { data: null, error: new Error(error) };
    }

    return {
      data: (data?.users || []).map(toUserProfile),
      error: null,
    };
  } catch (error) {
    logger.error('Error getting all users', error as Error);
    return { data: null, error: error as Error };
  }
};

/**
 * Gets all users for a specific service provider (for service provider admins)
 */
export const getServiceProviderUsers = async (): Promise<{
  data: UserProfile[] | null;
  error: Error | null;
}> => {
  try {
    // First get the current user's profile
    const { data: profile, error: profileError } = await getCurrentUserProfile();

    if (profileError || !profile || !profile.serviceprovider_id) {
      return {
        data: null,
        error: profileError || new Error('User has no associated service provider'),
      };
    }

    // Get all users and filter by service provider
    const { data: allUsers, error } = await getAllUsers(true);

    if (error || !allUsers) {
      return { data: null, error };
    }

    const filteredUsers = allUsers.filter(
      (user) => user.serviceprovider_id === profile.serviceprovider_id
    );

    return { data: filteredUsers, error: null };
  } catch (error) {
    logger.error('Error getting service provider users', error as Error);
    return { data: null, error: error as Error };
  }
};

/**
 * Updates the current user's profile metadata
 */
export const updateCurrentUserProfile = async (profileData: {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  countryCode: string;
}): Promise<{ success: boolean; error?: Error }> => {
  try {
    logger.info('Updating user profile');

    const input: UpdateUserInput = {
      fullName: `${profileData.firstName.trim()} ${profileData.lastName.trim()}`,
      phoneNumber: `${profileData.countryCode}${profileData.phoneNumber.trim()}`,
    };

    const response = await graphqlClient.authenticatedMutate<{
      updateUserProfile: { user: GraphQLUser };
    }>(UPDATE_USER_PROFILE_MUTATION, { input });

    const { error } = extractData(response);

    if (error) {
      logger.error('Error updating profile', undefined, { error });
      return { success: false, error: new Error(error) };
    }

    // Clear cache to force refresh
    clearUserProfileCache();

    logger.info('User profile updated successfully');
    return { success: true };
  } catch (error) {
    logger.error('Exception in updateCurrentUserProfile', error as Error);
    return { success: false, error: error as Error };
  }
};

// ============================================
// User Management Mutations
// ============================================

/**
 * Map frontend role to GraphQL role format
 */
function toGraphQLRole(role: 'customer' | 'serviceprovideradmin' | 'superadmin'): UserRole {
  const roleMap: Record<string, UserRole> = {
    customer: 'CUSTOMER',
    serviceprovideradmin: 'SERVICE_PROVIDER_ADMIN',
    superadmin: 'SUPER_ADMIN',
  };
  return roleMap[role] || 'CUSTOMER';
}

/**
 * Updates a user's role (superadmin only)
 */
export const updateUserRole = async (
  userId: string,
  newRole: 'customer' | 'serviceprovideradmin' | 'superadmin',
  serviceProviderId?: string
): Promise<{ data: UserProfile | null; error: Error | null }> => {
  try {
    logger.info('updateUserRole called', { userId, newRole, serviceProviderId });

    const input: UpdateUserRoleInput = {
      userId,
      newRole: toGraphQLRole(newRole),
      serviceProviderId,
    };

    const response = await graphqlClient.authenticatedMutate<UpdateUserRoleResponse>(
      UPDATE_USER_ROLE_MUTATION,
      { input }
    );

    const { data, error } = extractData(response);

    if (error) {
      logger.error('Error updating user role', undefined, { error, userId });
      return { data: null, error: new Error(error) };
    }

    if (!data?.updateUserRole?.user) {
      return { data: null, error: new Error('No user returned from mutation') };
    }

    const profile = toUserProfile(data.updateUserRole.user);
    logger.info('User role updated', { userId: profile.id, role: profile.role });
    return { data: profile, error: null };
  } catch (error) {
    logger.error('Exception in updateUserRole', error as Error);
    return { data: null, error: error as Error };
  }
};

/**
 * Updates a user's active status (superadmin or service provider admin for their users)
 */
export const updateUserStatus = async (
  userId: string,
  isActive: boolean
): Promise<{ data: UserProfile | null; error: Error | null }> => {
  try {
    logger.info('updateUserStatus called', { userId, isActive });

    const input: UpdateUserStatusInput = {
      userId,
      isActive,
    };

    const response = await graphqlClient.authenticatedMutate<UpdateUserStatusResponse>(
      UPDATE_USER_STATUS_MUTATION,
      { input }
    );

    const { data, error } = extractData(response);

    if (error) {
      logger.error('Error updating user status', undefined, { error, userId });
      return { data: null, error: new Error(error) };
    }

    if (!data?.updateUserStatus?.user) {
      return { data: null, error: new Error('No user returned from mutation') };
    }

    const profile = toUserProfile(data.updateUserStatus.user);
    logger.info('User status updated', { userId: profile.id, isActive: profile.is_active });
    return { data: profile, error: null };
  } catch (error) {
    logger.error('Exception in updateUserStatus', error as Error);
    return { data: null, error: error as Error };
  }
};

/**
 * Assigns a user to a service provider (superadmin only)
 */
export const assignUserToServiceProvider = async (
  userId: string,
  serviceProviderId: string
): Promise<{ data: UserProfile | null; error: Error | null }> => {
  try {
    logger.info('assignUserToServiceProvider called', { userId, serviceProviderId });

    const input: AssignUserToServiceProviderInput = {
      userId,
      serviceProviderId,
    };

    const response = await graphqlClient.authenticatedMutate<AssignUserToServiceProviderResponse>(
      ASSIGN_USER_TO_SERVICE_PROVIDER_MUTATION,
      { input }
    );

    const { data, error } = extractData(response);

    if (error) {
      logger.error('Error assigning user to service provider', undefined, { error, userId, serviceProviderId });
      return { data: null, error: new Error(error) };
    }

    if (!data?.assignUserToServiceProvider?.user) {
      return { data: null, error: new Error('No user returned from mutation') };
    }

    const profile = toUserProfile(data.assignUserToServiceProvider.user);
    logger.info('User assigned to service provider', { userId: profile.id, serviceProviderId: profile.serviceprovider_id });
    return { data: profile, error: null };
  } catch (error) {
    logger.error('Exception in assignUserToServiceProvider', error as Error);
    return { data: null, error: error as Error };
  }
};

// ============================================
// Admin Functions
// ============================================

/**
 * Creates a new service provider and an admin user for it (superadmin only)
 */
export const createServiceProviderAdmin = async (
  serviceproviderName: string,
  serviceKey: string,
  contactEmail: string,
  contactPhone: string,
  adminEmail: string,
  adminPassword: string,
  adminName: string
): Promise<{
  data: { user: unknown; profile: UserProfile; serviceProvider: ServiceProvider } | null;
  error: Error | null;
}> => {
  try {
    logger.info('createServiceProviderAdmin called');

    // 1. Create the service provider
    const spResponse = await graphqlClient.authenticatedMutate<{
      createServiceProvider: { serviceProvider: GraphQLServiceProvider };
    }>(CREATE_SERVICE_PROVIDER_MUTATION, {
      input: {
        serviceProviderName: serviceproviderName,
        serviceKey,
        contactEmail,
        contactPhone,
        primaryColor: '#3b82f6',
        isActive: true,
      },
    });

    const { data: spData, error: spError } = extractData(spResponse);

    if (spError || !spData?.createServiceProvider?.serviceProvider) {
      return {
        data: null,
        error: new Error(spError || 'Failed to create service provider'),
      };
    }

    const serviceProvider = toServiceProvider(spData.createServiceProvider.serviceProvider);
    logger.info('Service provider created', { id: serviceProvider.id, name: serviceProvider.serviceprovider_name });

    // 2. Register the admin user
    const userResponse = await graphqlClient.mutate<{
      register: { token: string; user: GraphQLUser };
    }>(REGISTER_MUTATION, {
      input: {
        email: adminEmail,
        password: adminPassword,
        fullName: adminName,
      },
    });

    const { data: userData, error: userError } = extractData(userResponse);

    if (userError || !userData?.register?.user) {
      return {
        data: null,
        error: new Error(userError || 'Failed to create admin user'),
      };
    }

    const profile = toUserProfile(userData.register.user);
    logger.info('Admin user created', { userId: profile.id, email: profile.email });

    // Note: Linking user to service provider would need a separate mutation
    // For now, return what we have

    return {
      data: {
        user: userData.register.user,
        profile,
        serviceProvider,
      },
      error: null,
    };
  } catch (err) {
    logger.error('Unexpected error in createServiceProviderAdmin', err as Error);
    return { data: null, error: err as Error };
  }
};

/**
 * Alias for createServiceProviderAdmin (used in some components)
 */
export const createBaseService = createServiceProviderAdmin;

/**
 * Creates a superadmin user (can only be called by existing superadmins or via trusted environment)
 */
export const createSuperAdmin = async (
  email: string,
  password: string,
  fullName: string
): Promise<{ data: { user: unknown; profile: UserProfile } | null; error: Error | null }> => {
  try {
    const response = await graphqlClient.mutate<{
      register: { token: string; user: GraphQLUser };
    }>(REGISTER_MUTATION, {
      input: {
        email,
        password,
        fullName,
      },
    });

    const { data, error } = extractData(response);

    if (error || !data?.register?.user) {
      return {
        data: null,
        error: new Error(error || 'Failed to create user'),
      };
    }

    const profile = toUserProfile(data.register.user);
    logger.info('Superadmin created', { userId: profile.id, email: profile.email });

    // Note: Setting superadmin role would need a separate admin mutation

    return {
      data: {
        user: data.register.user,
        profile,
      },
      error: null,
    };
  } catch (err) {
    logger.error('Unexpected error in createSuperAdmin', err as Error);
    return { data: null, error: err as Error };
  }
};

// ============================================
// Debug Functions
// ============================================

/**
 * Debug function to check user roles by email address
 */
export const checkUserRolesByEmail = async (
  email: string
): Promise<{
  user: unknown | null;
  profile: UserProfile | null;
  roles: UserRoles;
  serviceProvider: ServiceProvider | null;
  error?: Error;
}> => {
  logger.debug('Checking roles for email', { email });

  try {
    // Get all users and find by email
    const { data: users, error } = await getAllUsers(true);

    if (error || !users) {
      return {
        user: null,
        profile: null,
        roles: {
          isSuperAdmin: false,
          isServiceProviderAdmin: false,
          isBaseService: false,
          isCustomer: false,
        },
        serviceProvider: null,
        error: error || new Error('Could not fetch users'),
      };
    }

    const profile = users.find((u) => u.email === email);

    if (!profile) {
      logger.warn('No user found with email', { email });
      return {
        user: null,
        profile: null,
        roles: {
          isSuperAdmin: false,
          isServiceProviderAdmin: false,
          isBaseService: false,
          isCustomer: false,
        },
        serviceProvider: null,
        error: new Error('User not found'),
      };
    }

    logger.debug('User profile found', { email, role: profile.role });

    // Determine roles
    const isSuperAdmin = profile.role === 'superadmin';
    const isServiceProviderAdmin = profile.role === 'serviceprovideradmin';
    const isCustomer = profile.role === 'customer';

    // If user is service provider admin, also get service provider info
    let serviceProvider: ServiceProvider | null = null;
    if (isServiceProviderAdmin && profile.serviceprovider_id) {
      const { data: providers } = await getAllServiceProviders(true);
      if (providers) {
        serviceProvider = providers.find((sp) => sp.id === profile.serviceprovider_id) || null;
        if (serviceProvider) {
          logger.debug('Service provider found', { name: serviceProvider.serviceprovider_name });
        }
      }
    }

    return {
      user: { id: profile.id, email: profile.email },
      profile,
      roles: {
        isSuperAdmin,
        isServiceProviderAdmin,
        isBaseService: isServiceProviderAdmin,
        isCustomer,
      },
      serviceProvider,
    };
  } catch (error) {
    logger.error('Unexpected error checking user roles by email', error as Error);
    return {
      user: null,
      profile: null,
      roles: {
        isSuperAdmin: false,
        isServiceProviderAdmin: false,
        isBaseService: false,
        isCustomer: false,
      },
      serviceProvider: null,
      error: error as Error,
    };
  }
};

// ============================================
// Default Export
// ============================================

export default {
  getCurrentUserProfile,
  hasRole,
  isSuperAdmin,
  isServiceProviderAdmin,
  isBaseService,
  getCurrentUserServiceProvider,
  getAllServiceProviders,
  getServiceProviderByKey,
  createServiceProvider,
  updateServiceProvider,
  deleteServiceProvider,
  getAllUsers,
  getServiceProviderUsers,
  updateCurrentUserProfile,
  updateUserRole,
  updateUserStatus,
  assignUserToServiceProvider,
  createServiceProviderAdmin,
  createBaseService,
  createSuperAdmin,
  checkUserRolesByEmail,
  clearUserProfileCache,
};
